import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useActiveOrg } from '../../providers/useActiveOrg';
import { useTypedOpenApi } from '../../providers/useTypedOpenApi';
import {
  type BillingDetailsInput,
  type BillingInfo,
  cancelRenewal,
  cancelScheduledUpdate,
  createCheckoutSession,
  createSetupSession,
  downgradePlan,
  fetchBillingInfo,
  fetchBillingPortalUrl,
  fetchCheckoutResult,
  fetchInvoices,
  fetchScheduledUpdate,
  fetchUpcomingInvoice,
  type PaidPlanSlug,
  previewUpgrade,
  resumeRenewal,
  savePaymentMethod,
  updateBillingDetails,
  upgradePlan,
} from './billingApi';

/**
 * Query keys for the active organization's billing.
 *
 * Keyed by the Clerk org id: the answer belongs to the organization, and a switch must never show
 * the last organization's invoice.
 */
export const billingKeys = {
  all: (clerkOrgId: string | null) => ['subscription-billing', clerkOrgId] as const,
  upcomingInvoice: (clerkOrgId: string | null) => [...billingKeys.all(clerkOrgId), 'upcoming-invoice'] as const,
  scheduledUpdate: (clerkOrgId: string | null) => [...billingKeys.all(clerkOrgId), 'scheduled-update'] as const,
  invoices: (clerkOrgId: string | null) => [...billingKeys.all(clerkOrgId), 'invoices'] as const,
  billingInfo: (clerkOrgId: string | null) => [...billingKeys.all(clerkOrgId), 'billing-info'] as const,
  upgradePreview: (clerkOrgId: string | null, plan: PaidPlanSlug) =>
    [...billingKeys.all(clerkOrgId), 'upgrade-preview', plan] as const,
  checkout: (clerkOrgId: string | null, plan: PaidPlanSlug) =>
    [...billingKeys.all(clerkOrgId), 'checkout', plan] as const,
  checkoutResult: (clerkOrgId: string | null, sessionId: string) =>
    [...billingKeys.all(clerkOrgId), 'checkout-result', sessionId] as const,
} as const;

/** Stripe's answers change only when someone changes the plan, which invalidates them. */
const STALE_TIME = 5 * 60 * 1000;

/**
 * The next invoice for the organization's paid plan.
 *
 * Pass `enabled` only for the owner on a paid plan: the route refuses everyone else, and Free has
 * no subscription to invoice.
 */
export const useUpcomingInvoiceQuery = ({ enabled }: { enabled: boolean }) => {
  const { apiClient } = useTypedOpenApi();
  const { clerkOrgId } = useActiveOrg();

  return useQuery({
    queryKey: billingKeys.upcomingInvoice(clerkOrgId),
    queryFn: () => fetchUpcomingInvoice(apiClient),
    enabled: enabled && clerkOrgId !== null,
    staleTime: STALE_TIME,
    retry: false,
  });
};

/** The downgrade scheduled for the end of the period, `null` when there is none. Same `enabled` rule. */
export const useScheduledUpdateQuery = ({ enabled }: { enabled: boolean }) => {
  const { apiClient } = useTypedOpenApi();
  const { clerkOrgId } = useActiveOrg();

  return useQuery({
    queryKey: billingKeys.scheduledUpdate(clerkOrgId),
    queryFn: () => fetchScheduledUpdate(apiClient),
    enabled: enabled && clerkOrgId !== null,
    staleTime: STALE_TIME,
    retry: false,
  });
};

/** One request for the card and the billing details, which come from the same route. */
const useBillingInfoQuery = <TSelected>(enabled: boolean, select: (info: BillingInfo) => TSelected) => {
  const { apiClient } = useTypedOpenApi();
  const { clerkOrgId } = useActiveOrg();

  return useQuery({
    queryKey: billingKeys.billingInfo(clerkOrgId),
    queryFn: () => fetchBillingInfo(apiClient),
    select,
    enabled: enabled && clerkOrgId !== null,
    staleTime: STALE_TIME,
    retry: false,
  });
};

const selectPaymentMethod = (info: BillingInfo) => info.paymentMethod;

const selectDetails = (info: BillingInfo) => info.details;

/** The card or account the plan is charged to, `null` when there is none. Same `enabled` rule. */
export const usePaymentMethodQuery = ({ enabled }: { enabled: boolean }) =>
  useBillingInfoQuery(enabled, selectPaymentMethod);

/** The name, address, email and VAT ID on the invoices. Same `enabled` rule. */
export const useBillingDetailsQuery = ({ enabled }: { enabled: boolean }) =>
  useBillingInfoQuery(enabled, selectDetails);

/**
 * Changes the billing details; the Billing tab shows the saved ones at once. Read a failure's field
 * messages with `billingFieldErrors`.
 */
export const useUpdateBillingDetailsMutation = () => {
  const { apiClient } = useTypedOpenApi();
  const { clerkOrgId } = useActiveOrg();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: BillingDetailsInput) => updateBillingDetails(apiClient, input),
    onSuccess: (info) => queryClient.setQueryData(billingKeys.billingInfo(clerkOrgId), info),
    // A new address can change the VAT on the next invoice.
    onSettled: () => queryClient.invalidateQueries({ queryKey: billingKeys.upcomingInvoice(clerkOrgId) }),
  });
};

/**
 * A setup Checkout Session for the card form, coming back to `returnUrl` from a payment method that
 * leaves the page. A mutation, not a query: each opening of the form gets its own, and none is
 * kept for the next.
 */
export const useCreateSetupSessionMutation = () => {
  const { apiClient } = useTypedOpenApi();

  return useMutation({ mutationFn: (returnUrl: string) => createSetupSession(apiClient, returnUrl) });
};

/**
 * Makes the card a completed setup Checkout Session saved the one charged. The card shows at once,
 * without waiting for the refetch: the trial warning goes away on success.
 */
export const useSavePaymentMethodMutation = () => {
  const { apiClient } = useTypedOpenApi();
  const { clerkOrgId } = useActiveOrg();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (checkoutSessionId: string) => savePaymentMethod(apiClient, checkoutSessionId),
    onSuccess: (paymentMethod) =>
      queryClient.setQueryData<BillingInfo>(billingKeys.billingInfo(clerkOrgId), (info) =>
        info ? { ...info, paymentMethod } : info,
      ),
    // The next invoice can answer once a card is on file.
    onSettled: () => queryClient.invalidateQueries({ queryKey: billingKeys.upcomingInvoice(clerkOrgId) }),
  });
};

/**
 * A billing change, then a refetch of what it changed.
 *
 * The plan itself comes back through sync once Stripe's webhook has updated the row, so the
 * caller shows the result until the synced row agrees.
 */
const useBillingMutation = <TResult>(
  mutationFn: (apiClient: ReturnType<typeof useTypedOpenApi>['apiClient']) => Promise<TResult>,
) => {
  const { apiClient } = useTypedOpenApi();
  const { clerkOrgId } = useActiveOrg();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => mutationFn(apiClient),
    onSettled: () => queryClient.invalidateQueries({ queryKey: billingKeys.all(clerkOrgId) }),
  });
};

export const useCancelRenewalMutation = () => useBillingMutation(cancelRenewal);

export const useResumeRenewalMutation = () => useBillingMutation(resumeRenewal);

export const useCancelScheduledUpdateMutation = () => useBillingMutation(cancelScheduledUpdate);

/**
 * The organization's invoices, a page at a time. Same `enabled` rule as the others, and the route
 * also wants the plan to be active.
 */
export const useInvoicesQuery = ({ enabled }: { enabled: boolean }) => {
  const { apiClient } = useTypedOpenApi();
  const { clerkOrgId } = useActiveOrg();

  return useInfiniteQuery({
    queryKey: billingKeys.invoices(clerkOrgId),
    queryFn: ({ pageParam }) => fetchInvoices(apiClient, pageParam ?? undefined),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: enabled && clerkOrgId !== null,
    staleTime: STALE_TIME,
    retry: false,
  });
};

/**
 * Sends the owner to Stripe's billing portal, which brings them back to the page they left.
 *
 * The link is fetched on click rather than up front: a portal session expires after a few minutes.
 */
export const useOpenBillingPortalMutation = () => {
  const { apiClient } = useTypedOpenApi();

  return useMutation({
    mutationFn: async () => {
      const url = await fetchBillingPortalUrl(apiClient, window.location.href);

      // Stripe's page, not a route of this app: the router can't go there.
      window.location.assign(url);
    },
  });
};

/**
 * What upgrading to `plan` costs today and at renewal, from Stripe. Pass `enabled` only for the
 * owner of a paid plan below `plan`, with nothing scheduled and no trial: the route refuses the rest.
 */
export const useUpgradePreviewQuery = ({ plan, enabled }: { plan: PaidPlanSlug; enabled: boolean }) => {
  const { apiClient } = useTypedOpenApi();
  const { clerkOrgId } = useActiveOrg();

  return useQuery({
    queryKey: billingKeys.upgradePreview(clerkOrgId, plan),
    queryFn: () => previewUpgrade(apiClient, plan),
    enabled: enabled && clerkOrgId !== null,
    // Proration moves by the day, and a preview is only a moment's quote.
    staleTime: 60 * 1000,
    retry: false,
  });
};

export const useUpgradePlanMutation = () => {
  const { apiClient } = useTypedOpenApi();
  const { clerkOrgId } = useActiveOrg();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (plan: PaidPlanSlug) => upgradePlan(apiClient, plan),
    onSettled: () => queryClient.invalidateQueries({ queryKey: billingKeys.all(clerkOrgId) }),
  });
};

export const useDowngradePlanMutation = () => {
  const { apiClient } = useTypedOpenApi();
  const { clerkOrgId } = useActiveOrg();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (plan: PaidPlanSlug) => downgradePlan(apiClient, plan),
    onSettled: () => queryClient.invalidateQueries({ queryKey: billingKeys.all(clerkOrgId) }),
  });
};

/**
 * A Stripe checkout for `plan`, made once per visit to the checkout page.
 *
 * A query rather than a mutation so the page has one session however often it renders; it is
 * never refetched or kept, since each fetch makes a new session in Stripe. Pass `enabled` only for
 * the owner of an organization with no paid plan: the route refuses a second subscription.
 */
export const useCheckoutSessionQuery = ({
  plan,
  returnUrl,
  enabled,
}: {
  plan: PaidPlanSlug;
  returnUrl: string;
  enabled: boolean;
}) => {
  const { apiClient } = useTypedOpenApi();
  const { clerkOrgId } = useActiveOrg();

  return useQuery({
    queryKey: billingKeys.checkout(clerkOrgId, plan),
    queryFn: () => createCheckoutSession(apiClient, plan, returnUrl),
    enabled: enabled && clerkOrgId !== null,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

/** How the checkout `sessionId` ended. `null` skips it. */
export const useCheckoutResultQuery = (sessionId: string | null) => {
  const { apiClient } = useTypedOpenApi();
  const { clerkOrgId } = useActiveOrg();

  return useQuery({
    queryKey: billingKeys.checkoutResult(clerkOrgId, sessionId ?? ''),
    queryFn: () => fetchCheckoutResult(apiClient, sessionId ?? ''),
    enabled: sessionId !== null && clerkOrgId !== null,
    retry: false,
  });
};
